import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
  Label,
} from '../ui';
import type { Message } from '../../types';
import {
  estimateConversationTokens,
  findMessagesToDeleteForTokenTarget,
  formatTokenCount,
} from '../../utils';
import { Loader2 } from 'lucide-react';
import { llmApiClient } from '../../api/network';

type DeleteMode = 'all' | 'resize' | 'keep-last' | 'compress';

interface DeleteMessagesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messages: Message[];
  currentTokens: number;
  maxContextTokens: number;
  modelMaxContextTokens?: number;
  onDelete: (messagesToKeep: Message[]) => void;
  // For compression feature
  apiUrl?: string;
  apiKey?: string;
  model?: string;
}

export function DeleteMessagesDialog({
  open,
  onOpenChange,
  messages,
  currentTokens,
  maxContextTokens,
  modelMaxContextTokens,
  onDelete,
  apiUrl,
  apiKey,
  model,
}: DeleteMessagesDialogProps) {
  const [mode, setMode] = useState<DeleteMode>('all');
  const [resizeTarget, setResizeTarget] = useState<string>(
    Math.floor(maxContextTokens * 0.5).toString()
  );
  const [keepLastCount, setKeepLastCount] = useState<string>('10');
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressError, setCompressError] = useState<string | null>(null);

  const handleCompress = async () => {
    if (!apiUrl || !model) {
      setCompressError('API URL and model are required for compression');
      return;
    }

    setIsCompressing(true);
    setCompressError(null);

    try {
      // Build the conversation text for summarization
      const conversationText = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
        .join('\n\n');

      const systemPrompt = `You are a conversation summarizer. Your task is to create a detailed summary of the following conversation that preserves all key information, decisions, code snippets, technical details, and context. The summary should be comprehensive enough that someone reading it would understand:
- What was discussed
- What decisions were made
- Any code or technical solutions provided
- Important context or constraints mentioned
- The current state of any tasks or projects

Format the summary as a system message that can be used to continue the conversation. Be thorough and detailed - this summary will replace the entire conversation history.`;

      interface CompletionResponse {
        choices?: Array<{ message?: { content?: string } }>;
      }

      const { data } = await llmApiClient.post<CompletionResponse>(
        `${apiUrl}/v1/chat/completions`,
        {
          model,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: `Please summarize this conversation in detail:\n\n${conversationText}` },
          ],
          temperature: 0.3,
        },
        {
          timeout: 120_000, // 2 minutes for summarization
          headers: {
            ...(apiKey && { Authorization: `Bearer ${apiKey}` }),
          },
        }
      );

      const summary = data.choices?.[0]?.message?.content;

      if (!summary) {
        throw new Error('No summary returned from API');
      }

      // Create a new system message with the summary
      const summaryMessage: Message = {
        id: `summary-${Date.now()}`,
        role: 'system',
        content: `[Conversation Summary]\n\n${summary}`,
        createdAt: Date.now(),
      };

      onDelete([summaryMessage]);
      onOpenChange(false);
    } catch (error) {
      setCompressError(error instanceof Error ? error.message : 'Failed to compress conversation');
    } finally {
      setIsCompressing(false);
    }
  };

  const handleDelete = () => {
    if (mode === 'compress') {
      handleCompress();
      return;
    }

    let messagesToKeep: Message[] = [];

    switch (mode) {
      case 'all':
        messagesToKeep = [];
        break;

      case 'resize': {
        const targetTokens = parseInt(resizeTarget, 10);
        if (isNaN(targetTokens) || targetTokens < 0) {
          messagesToKeep = messages;
          break;
        }
        const tokensToRemove = currentTokens - targetTokens;
        if (tokensToRemove <= 0) {
          messagesToKeep = messages;
          break;
        }
        const deleteCount = findMessagesToDeleteForTokenTarget(messages, tokensToRemove);
        messagesToKeep = messages.slice(deleteCount);
        break;
      }

      case 'keep-last': {
        const keepCount = parseInt(keepLastCount, 10);
        if (isNaN(keepCount) || keepCount < 0) {
          messagesToKeep = messages;
          break;
        }
        messagesToKeep = messages.slice(-keepCount);
        break;
      }
    }

    onDelete(messagesToKeep);
    onOpenChange(false);
  };

  // Calculate preview of what will be deleted
  const getPreview = (): { willDelete: number; willKeep: number; tokensAfter: number; isCompress?: boolean } => {
    switch (mode) {
      case 'all':
        return {
          willDelete: messages.length,
          willKeep: 0,
          tokensAfter: 0,
        };

      case 'compress':
        return {
          willDelete: messages.length,
          willKeep: 1,
          tokensAfter: 0, // Unknown until compression completes
          isCompress: true,
        };

      case 'resize': {
        const targetTokens = parseInt(resizeTarget, 10);
        if (isNaN(targetTokens) || targetTokens < 0) {
          return { willDelete: 0, willKeep: messages.length, tokensAfter: currentTokens };
        }
        const tokensToRemove = currentTokens - targetTokens;
        if (tokensToRemove <= 0) {
          return { willDelete: 0, willKeep: messages.length, tokensAfter: currentTokens };
        }
        const deleteCount = findMessagesToDeleteForTokenTarget(messages, tokensToRemove);
        const kept = messages.slice(deleteCount);
        return {
          willDelete: deleteCount,
          willKeep: kept.length,
          tokensAfter: estimateConversationTokens(kept),
        };
      }

      case 'keep-last': {
        const keepCount = parseInt(keepLastCount, 10);
        if (isNaN(keepCount) || keepCount < 0) {
          return { willDelete: 0, willKeep: messages.length, tokensAfter: currentTokens };
        }
        const effectiveKeep = Math.min(keepCount, messages.length);
        const kept = messages.slice(-effectiveKeep);
        return {
          willDelete: messages.length - effectiveKeep,
          willKeep: effectiveKeep,
          tokensAfter: estimateConversationTokens(kept),
        };
      }
    }
  };

  const preview = getPreview();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Messages</DialogTitle>
          <DialogDescription>
            <span>Current: {formatTokenCount(currentTokens)} tokens ({messages.length} messages)</span>
            {maxContextTokens > 0 && (
              <span className="block mt-1">
                Configured: {formatTokenCount(maxContextTokens)}
                {modelMaxContextTokens && modelMaxContextTokens !== maxContextTokens && (
                  <> / Max: {formatTokenCount(modelMaxContextTokens)}</>
                )}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* All option */}
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id="delete-all"
              name="delete-mode"
              checked={mode === 'all'}
              onChange={() => setMode('all')}
              className="h-4 w-4"
            />
            <Label htmlFor="delete-all" className="cursor-pointer">
              Delete all messages
            </Label>
          </div>

          {/* Resize to target tokens */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="delete-resize"
                name="delete-mode"
                checked={mode === 'resize'}
                onChange={() => setMode('resize')}
                className="h-4 w-4"
              />
              <Label htmlFor="delete-resize" className="cursor-pointer">
                Resize to token limit
              </Label>
            </div>
            {mode === 'resize' && (
              <div className="ml-6 flex items-center gap-2">
                <Input
                  type="number"
                  value={resizeTarget}
                  onChange={(e) => setResizeTarget(e.target.value)}
                  className="w-28"
                  min={0}
                  max={maxContextTokens || undefined}
                />
                <span className="text-sm text-muted-foreground">tokens</span>
              </div>
            )}
          </div>

          {/* Keep last N messages */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="delete-keep-last"
                name="delete-mode"
                checked={mode === 'keep-last'}
                onChange={() => setMode('keep-last')}
                className="h-4 w-4"
              />
              <Label htmlFor="delete-keep-last" className="cursor-pointer">
                Keep last N messages
              </Label>
            </div>
            {mode === 'keep-last' && (
              <div className="ml-6 flex items-center gap-2">
                <Input
                  type="number"
                  value={keepLastCount}
                  onChange={(e) => setKeepLastCount(e.target.value)}
                  className="w-20"
                  min={0}
                  max={messages.length}
                />
                <span className="text-sm text-muted-foreground">messages</span>
              </div>
            )}
          </div>

          {/* Compress/Summarize option */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="delete-compress"
                name="delete-mode"
                checked={mode === 'compress'}
                onChange={() => setMode('compress')}
                className="h-4 w-4"
                disabled={!apiUrl || !model}
              />
              <Label
                htmlFor="delete-compress"
                className={`cursor-pointer ${!apiUrl || !model ? 'text-muted-foreground' : ''}`}
              >
                Compress (summarize with AI)
              </Label>
            </div>
            {mode === 'compress' && (
              <p className="ml-6 text-xs text-muted-foreground">
                Uses AI to create a detailed summary of the conversation, replacing all messages with a single summary.
              </p>
            )}
            {compressError && mode === 'compress' && (
              <p className="ml-6 text-xs text-red-500">{compressError}</p>
            )}
          </div>

          {/* Preview */}
          <div className="rounded-md bg-muted p-3 text-sm">
            <div className="font-medium mb-1">Preview:</div>
            <div className="text-muted-foreground space-y-0.5">
              {preview.isCompress ? (
                <>
                  <div>Will summarize: {preview.willDelete} messages</div>
                  <div>Result: 1 system message with detailed summary</div>
                  <div className="text-xs italic">Token count will be determined after summarization</div>
                </>
              ) : (
                <>
                  <div>Will delete: {preview.willDelete} messages</div>
                  <div>Will keep: {preview.willKeep} messages</div>
                  <div>Tokens after: {formatTokenCount(preview.tokensAfter)}</div>
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-row justify-end space-x-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isCompressing}>
            Cancel
          </Button>
          <Button
            variant={mode === 'compress' ? 'default' : 'destructive'}
            onClick={handleDelete}
            disabled={preview.willDelete === 0 || isCompressing}
          >
            {isCompressing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Compressing...
              </>
            ) : mode === 'compress' ? (
              'Compress conversation'
            ) : (
              `Delete ${preview.willDelete} messages`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
